import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that shows a "FLAT X% APPLIED" badge computed from the
 * selected variant's price vs. compare-at price, and keeps itself in sync
 * when the shopper switches variants (metal, carat, etc.) without a full
 * page reload - mirrors the pattern used by the theme's own Product Price
 * block (assets/product-price.js).
 *
 * Also hosts the "+ ADDITIONAL X% OFF" pill so it renders beside the FLAT
 * pill in one row. That pill has no server-rendered state of its own -
 * it's driven entirely by the nearby Super Save Banner block's checkbox,
 * kept in sync via:
 *   1) a direct DOM read on connect (#syncSuperSaveBadge), so it's
 *      correct immediately even right after a variant-switch replaceWith
 *      wipes any previous client-side state, and
 *   2) a `supersave:toggle` custom event dispatched by
 *      assets/super-save-banner.js every time it re-renders (on click,
 *      on toggle success/failure, on variant change), so it updates
 *      instantly with no extra network round trip of its own.
 */
class FlatDiscountBadge extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.section?.addEventListener('supersave:toggle', this.#handleSuperSaveToggle);
    this.#syncSuperSaveBadge();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.section?.removeEventListener('supersave:toggle', this.#handleSuperSaveToggle);
  }

  #syncSuperSaveBadge() {
    const superSaveEl = this.section?.querySelector(
      `super-save-banner-component[data-product-id="${this.dataset.productId}"]`
    );
    if (!superSaveEl) return;

    const checkbox = /** @type {HTMLInputElement | null} */ (superSaveEl.querySelector('[ref="checkbox"]'));
    this.#updateAdditionalBadge({
      productId: superSaveEl.dataset.productId,
      checked: !!checkbox?.checked,
      percentage: superSaveEl.dataset.discountPercentage,
      template: superSaveEl.dataset.badgeTemplate,
    });
  }

  /**
   * @param {CustomEvent} event
   */
  #handleSuperSaveToggle = (event) => {
    this.#updateAdditionalBadge(event.detail ?? {});
  };

  /**
   * @param {{productId?: string, checked?: boolean, percentage?: string|number, template?: string}} detail
   */
  #updateAdditionalBadge({ productId, checked, percentage, template } = {}) {
    if (productId && productId !== this.dataset.productId) return;

    const badge = this.refs.additionalBadge;
    if (!badge) return;

    if (template && percentage != null) {
      badge.textContent = String(template).replace('[percentage]', String(percentage));
    }
    badge.classList.toggle('is-visible', !!checked);
    badge.setAttribute('aria-hidden', String(!checked));
  }

  /**
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then((/** @type {any} */ { detail }) => {
        if (!detail?.html) return;

        const newEl = detail.html.querySelector(
          `flat-discount-badge-component[data-block-id="${this.dataset.blockId}"]`
        );
        if (!newEl) return;

        this.replaceWith(newEl);
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[flat-discount-badge] promise rejected:', error);
      });
  };
}

if (!customElements.get('flat-discount-badge-component')) {
  customElements.define('flat-discount-badge-component', FlatDiscountBadge);
}
